import React from 'react';
import { 
  Container, 
  Typography, 
  Link, 
  Box, 
  IconButton, 
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Footer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const currentYear = new Date().getFullYear();
  
  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(to right, #0c2340, #1a365d)',
        color: 'white',
        pt: 8,
        pb: 4,
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Elements */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        right: -100,
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%)',
        borderRadius: '50%',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -80,
        left: -80,
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%)',
        borderRadius: '50%',
        zIndex: 0
      }} />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Footer Content */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4
        }}>
          {/* Company Information */}
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Right Track Technologies Limited
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3, maxWidth: '350px' }}>
              Process-Driven Company delivering superior, sustained and ground-breaking solutions to our valued clients.
            </Typography>
            
            {/* Social Icons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <IconButton 
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.3s ease',
                }} 
                aria-label="facebook"
              >
                <FacebookIcon />
              </IconButton>
              <IconButton 
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.3s ease',
                }} 
                aria-label="twitter"
              >
                <TwitterIcon />
              </IconButton>
              <IconButton 
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.3s ease',
                }} 
                aria-label="linkedin"
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton 
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.3s ease',
                }} 
                aria-label="instagram"
              >
                <InstagramIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Contact Information */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Head Office: 1 Norana Avenue, Favona, Auckland, postal code-2024, New Zealand
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Regional Office: 43325 John Michael Ter, Ashburn, VA 20148, USA
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  info@rttlnz.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Business Hours: 9:00 AM – 5:00 PM
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Monday – Saturday
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Quick Links */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Links
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr', 
              gap: 1 
            }}>
              <Link 
                component={RouterLink} 
                to="/" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Home
              </Link>
              <Link 
                component={RouterLink} 
                to="/about" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                About Us
              </Link>
              <Link 
                component={RouterLink} 
                to="/services" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/contact" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Contact
              </Link>
            </Box>

            {/* Services Links */}
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
              Our Services
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr', 
              gap: 1 
            }}>
              <Link 
                component={RouterLink} 
                to="/services/cmmi" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                CMMI Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/services/iso" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                ISO Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/services/digital" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Digital Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/services/training" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Training Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/services/other" 
                color="inherit" 
                underline="hover" 
                sx={{ 
                  display: 'block', 
                  py: 0.5,
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                    pl: 0.5,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Other Services
              </Link>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        {/* Copyright Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between',
          alignItems: { xs: 'center', sm: 'center' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.95rem' }}>
            Copyright © {currentYear} Right Track Technologies Limited. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: { xs: 2, sm: 0 } }}>
            <Link color="inherit" underline="hover" sx={{ opacity: 0.7, fontSize: '0.95rem' }}>
              Privacy Policy
            </Link>
            <Link color="inherit" underline="hover" sx={{ opacity: 0.7, fontSize: '0.95rem' }}>
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 